
import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    jmeno: '',
    email: '',
    telefon: '',
    sluzba: '',
    zprava: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const services = [
    { value: '', label: 'Vyberte službu' },
    { value: 'instalaterske-prace', label: 'Instalatérské práce' },
    { value: 'topenarske-prace', label: 'Topenářské práce' },
    { value: 'plynove-instalace', label: 'Plynové instalace' },
    { value: 'kanalizace', label: 'Kanalizace' },
    { value: 'havarijni-sluzba', label: 'Havarijní služba' },
    { value: 'servis-udrzba', label: 'Servis a údržba' },
    { value: 'jine', label: 'Jiné' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceSelect = (value: string) => {
    setFormData(prev => ({
      ...prev,
      sluzba: value
    }));
    setIsDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.zprava.length > 500) {
      alert('Zpráva nesmí překročit 500 znaků.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.jmeno,
            email: formData.email,
            phone: formData.telefon,
            service: formData.sluzba,
            message: formData.zprava
          }
        ]);
      
      if (error) {
        console.error('Chyba při ukládání:', error);
        setSubmitStatus('error');
      } else {
        setSubmitStatus('success');
        setFormData({
          jmeno: '',
          email: '',
          telefon: '',
          sluzba: '',
          zprava: ''
        });
      }
    } catch (error) {
      console.error('Chyba:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedService = services.find(s => s.value === formData.sluzba);

  return (
    <section id="kontakt" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            <strong>Kontakt</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Máte dotaz nebo potřebujete naše služby? Neváhejte nás kontaktovat. Rádi vám pomůžeme.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="h-full">
            <div className="bg-white rounded-xl shadow-lg p-8 h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Kontaktní informace</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Telefon</h4>
                    <a href="tel:+420720520035" className="text-blue-600 hover:text-blue-700 text-lg font-medium">
                      720 520 035
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Non stop - havarijní služba</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">E-mail</h4>
                    <a href="mailto:instatop.snajdr@post.cz" className="text-blue-600 hover:text-blue-700">
                      instatop.snajdr@post.cz
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Adresa</h4>
                    <p className="text-gray-700">Vinec 57</p>
                    <p className="text-gray-700">293 01 Mladá Boleslav</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-instagram-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Instagram</h4>
                    <a href="https://www.instagram.com/topinstal_snajdr/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700">
                      @topinstal_snajdr
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Sledujte naše práce</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-facebook-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Facebook</h4>
                    <a href="https://www.facebook.com/profile.php?id=61560758253104" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                      TopInstal Snajdr
                    </a>
                    <p className="text-gray-600 text-sm mt-1">Navštivte náš profil</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-building-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Firma</h4>
                    <p className="text-gray-700">TopInstal Snajdr</p>
                    <p className="text-gray-600 text-sm mt-1">IČ: 21645175</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="h-full">
            <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Kontaktní formulář</h3>
              
              <form id="kontakt-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
                <div>
                  <label htmlFor="jmeno" className="block text-sm font-medium text-gray-700 mb-2">
                    Jméno a příjmení *
                  </label>
                  <input
                    type="text"
                    id="jmeno"
                    name="jmeno"
                    value={formData.jmeno}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Vaše jméno"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="vas@email.cz"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="+420 123 456 789"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="sluzba" className="block text-sm font-medium text-gray-700 mb-2">
                    Typ služby
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white text-left flex items-center justify-between cursor-pointer hover:border-gray-400 transition-colors"
                    >
                      <span className={formData.sluzba ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedService?.label || 'Vyberte službu'}
                      </span>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    
                    {isDropdownOpen && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setIsDropdownOpen(false)}
                        ></div>
                        <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {services.map((service, index) => (
                            <button
                              key={service.value}
                              type="button"
                              onClick={() => handleServiceSelect(service.value)}
                              className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center justify-between ${
                                index !== services.length - 1 ? 'border-b border-gray-100' : ''
                              } ${
                                formData.sluzba === service.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                              }`}
                            >
                              <span>{service.label}</span>
                              {formData.sluzba === service.value && (
                                <i className="ri-check-line text-blue-600"></i>
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    
                    <input
                      type="hidden"
                      id="sluzba"
                      name="sluzba"
                      value={formData.sluzba}
                    />
                  </div>
                </div>
                
                <div className="flex-1 flex flex-col">
                  <label htmlFor="zprava" className="block text-sm font-medium text-gray-700 mb-2">
                    Zpráva *
                  </label>
                  <textarea
                    id="zprava"
                    name="zprava"
                    value={formData.zprava}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none flex-1"
                    placeholder="Popište váš problém nebo požadavek..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.zprava.length}/500 znaků
                  </p>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2"></i>
                      <p className="text-green-800 text-sm">Zpráva byla úspěšně odeslána. Brzy se vám ozveme!</p>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 text-sm">Došlo k chybě při odesílání. Zkuste to prosím znovu.</p>
                    </div>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-full mt-auto"
                  onClick={isSubmitting ? undefined : undefined}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Odesílání...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Odeslat zprávu
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
